// Wiring. Builds the engine once, then swaps interfaces over the top of it --
// the pattern keeps playing across a switch, which is the demo moment the
// whole design is arranged around.

import './style.css'
import { createEngine } from './engine/engine.js'
import { createApi } from './api.js'
import { createParams } from './params.js'
import { createLogger } from './logger.js'
import { createPanel } from './panel.js'
import { createSession } from './study/session.js'
import { INTERFACES, getInterface, STUDY_INTERFACE_IDS } from './interfaces/index.js'
import { syncClocks, nextFrame } from './clock.js'

const app = document.querySelector('#app')

const params = createParams()
const engine = createEngine()
const logger = createLogger({ params })
const api = createApi({ engine, logger, params })

// --- shell ----------------------------------------------------------------

const topbar = document.createElement('header')
topbar.className = 'topbar'
topbar.innerHTML = `
  <h1>nano<span>jam</span></h1>
  <nav class="modes" aria-label="interface"></nav>
  <div class="spacer"></div>
  <div class="transport">
    <button class="play" aria-pressed="false">play</button>
  </div>`

const stage = document.createElement('main')
stage.id = 'stage'

const ifaceRoot = document.createElement('section')
ifaceRoot.className = 'iface-root'

const studyHost = document.createElement('section')
studyHost.className = 'study-host'
studyHost.hidden = true

stage.append(ifaceRoot, studyHost)
app.append(topbar, stage)

const modes = topbar.querySelector('.modes')
const playBtn = topbar.querySelector('.play')

// --- interface switching --------------------------------------------------

let current = null       // { id, handle }

function mountInterface(id) {
  const def = getInterface(id)
  if (!def) return
  current?.handle?.unmount?.()
  ifaceRoot.replaceChildren()
  const handle = def.mount({ api, root: ifaceRoot, params, logger })
  current = { id, handle }
  api._setSource(id)
  for (const b of modes.children) b.setAttribute('aria-pressed', String(b.dataset.id === id))
  // The interface is in the hash so a lecture slide can link straight to one.
  if (params.get('ui.interface') !== id) history.replaceState(null, '', locationWithInterface(id))
}

function locationWithInterface(id) {
  const u = new URL(location.href)
  const h = new URLSearchParams(u.hash.slice(1))
  h.set('ui.interface', id)
  u.hash = h.toString()
  return u.toString()
}

for (const def of INTERFACES) {
  const b = document.createElement('button')
  b.textContent = def.label
  b.dataset.id = def.id
  b.setAttribute('aria-pressed', 'false')
  b.onclick = () => { api.unlock(); mountInterface(def.id) }
  modes.append(b)
}

playBtn.onclick = async () => {
  await api.unlock()
  if (engine.playing) api.stop()
  else api.start()
}

engine.onChange((e) => {
  if (e.type === 'transport') {
    playBtn.textContent = e.playing ? 'stop' : 'play'
    playBtn.classList.toggle('playing', e.playing)
    playBtn.setAttribute('aria-pressed', String(e.playing))
  }
})

// --- parameter panel ------------------------------------------------------

// The panel supplies its own toggle (a floating pill, plus `?` and Cmd/Ctrl+K),
// so the topbar deliberately does not duplicate it.
const panel = createPanel({ api, params, logger, root: app })

// --- study ----------------------------------------------------------------

const mountController = {
  async switchTo(id) { mountInterface(id); await nextFrame() },
  currentHandle: () => current?.handle,
  studyInterfaceIds: () => STUDY_INTERFACE_IDS,
}

let session = null

export async function runStudy() {
  studyHost.hidden = false
  session = createSession({ engine, api, params, logger, mount: mountController, host: studyHost })
  try { await session.run() } finally { session.dispose(); session = null }
}

// Exposed so the panel, a lecture console, or a URL can start a session
// without the free-play UI having to know the study exists.
window.nanojam = { api, params, logger, engine, runStudy, panel }

// --- first-gesture gate ---------------------------------------------------
// Audio needs a user gesture on every platform, and iOS hands back a suspended
// context surprisingly often, so this is deliberately unmissable.

const gate = document.createElement('div')
gate.className = 'gate'
gate.innerHTML = `
  <button>tap to start</button>
  <p>nanojam makes sound. Turn your volume up.</p>`
gate.querySelector('button').onclick = async () => {
  await api.unlock()
  syncClocks()
  gate.remove()
  const startId = params.get('ui.interface') || INTERFACES[0]?.id
  mountInterface(startId)
  if (params.get('study.condition')) runStudy()
}
document.body.append(gate)

// Keep the clocks honest across suspensions; iOS resets the relationship
// whenever the context is interrupted and resumed.
document.addEventListener('visibilitychange', () => { if (!document.hidden) syncClocks() })

// --- HMR ------------------------------------------------------------------
// Without this Vite full-reloads on every edit, tearing down the AudioContext
// and stopping the pattern. Instead, re-mount just the edited interface and
// leave the engine and the Transport running.

if (import.meta.hot) {
  import.meta.hot.accept('./interfaces/index.js', (mod) => {
    if (!mod || !current) return
    const def = mod.INTERFACES.find((i) => i.id === current.id)
    if (!def) return location.reload()
    current.handle?.unmount?.()
    ifaceRoot.replaceChildren()
    current = { id: def.id, handle: def.mount({ api, root: ifaceRoot, params, logger }) }
  })
  // The engine holds the audio graph; changing it genuinely does need a reload.
  import.meta.hot.accept('./engine/engine.js', () => location.reload())
}
